import { styled } from 'styled-components'

import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

export const PageWidth = styled.div`
  --paddingX: 1rem;

  margin: 0 auto;
  max-width: var(--maxPageWidth);
  width: calc(100% - var(--paddingX) * 2);

  @media ${Breakpoints.min.medium} {
    --paddingX: 1.5rem;
  }
`

/**
 * `PageWidth` with the gutter the global header uses, so content sits on the same left and right
 * edges as the logo above it.
 *
 * The header's gutter is fluid below `medium` — fe-core's `GlobalHeader` sets
 * `--headerPadding: clamp(1rem, 0.55rem + 2.27vw, 2rem)` there — while `PageWidth`'s is a flat
 * `1rem`. On a 390px phone that is 17.65px against 16px, so every page using `PageWidth` sits
 * slightly inside the header rather than lining up with it.
 *
 * The clamp is repeated rather than read from `--headerPadding`, which is declared on the header
 * element and so does not reach anything outside it. If fe-core changes that value this has to
 * change with it; there is no way to derive it, and nothing will fail if it drifts — the content
 * simply stops lining up.
 *
 * The value to check against: `--headerPadding` in fe-core's
 * `bstock-react/components/organisms/GlobalHeader/ui.ts`, on `HeaderContainer`. It is `1.5rem` by
 * default, this clamp under `Breakpoints.max.medium`, and `3rem` under `Breakpoints.min.large` —
 * home-portal overrides that last one back to `1.5rem` in `globalHeader/GlobalHeaderWrapper.tsx`,
 * which is why only the mobile value needs repeating here.
 */
export const HeaderAlignedPageWidth = styled(PageWidth)`
  @media ${Breakpoints.max.medium} {
    --paddingX: clamp(1rem, 0.55rem + 2.27vw, 2rem);
  }
`

/**
 * border-top and margin-top are fragile, specific to use in listings pages
 * below pagination and above footer. Refactor (extract) to share this cleanly.
 */
export const PageWidthText = styled(PageWidth)`
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;

  h2 {
    ${Typography.Title2}
  }

  p {
    ${Typography.Body1}
    color: ${Colors.Semantic.Neutral.very_dark_grey};
  }

  @media ${Breakpoints.max.medium} {
    margin-top: 2rem;
  }
`

/**
 * The page shell the My Account pages use: a flat `1rem` side gutter and a
 * `2rem` bottom gap. Twelve pages each declared this verbatim.
 *
 * Deliberately not built on `PageWidth`, which expresses its gutter as
 * `calc(100% - var(--paddingX) * 2)` and widens it to `1.5rem` from `medium`
 * up. Rebasing these pages onto it would move their left and right edges on
 * anything tablet-sized or larger — a visual change, not a refactor.
 * Reconciling the two is a design-system decision of its own.
 */
export const PageContainer = styled.div`
  max-width: var(--maxPageWidth);
  margin: 0 auto;
  padding: 0 1rem 2rem;
`
